import type { EpisodeOfCare, HumanName as FhirHumanName, Patient, Reference } from "@medplum/fhirtypes";
import { PATIENT_ENTITY_ID_SYSTEM, PRACTITIONER_ENTITY_ID_SYSTEM } from "@repo/api";
import { patientIdSchema } from "@repo/api/schemas";
import { type PatientId, Patient as PatientModel } from "@repo/domain";
import { PATIENT_HOLDING_URL } from "@repo/fhir-systems/extensions";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type { PatientRow } from "../fhir/types";

import type { Address, ContactPoint, HumanName, Identifier } from "./shared/types";

/**
 * Patient — normalized read-model.
 * `name` is the primary HumanName picked by data-store (use=official > first).
 * Additional names preserved in `otherNames` for UIs that need nickname/maiden/aliases.
 */
export interface PatientDto {
  patientId: PatientId;
  active: boolean;

  name: HumanName;
  otherNames?: HumanName[];

  gender?: "male" | "female" | "other" | "unknown";
  birthDate?: string;
  deceased?: boolean | string;

  telecom?: ContactPoint[];
  address?: Address[];

  identifiers?: Identifier[];
}

const PATIENT_COLLECTION = "patient";
const EPISODE_OF_CARE_COLLECTION = "episodeOfCare";

/** Case statuses that mean the patient has been discharged from that episode. */
const TERMINAL_CASE_STATUSES: ReadonlySet<EpisodeOfCare["status"]> = new Set([
  "finished",
  "cancelled",
  "entered-in-error",
]);

type CaseAssignment = Pick<EpisodeOfCare, "patient" | "careManager" | "status">;

/**
 * Identity lives in the reference's identifier, never in its literal. The literal
 * is a server row locator, so matching on it compares a domain id against a row id
 * and silently finds nothing.
 */
function domainId(ref: Reference | undefined, system: string): string | undefined {
  return ref?.identifier?.system === system ? ref.identifier.value : undefined;
}

function patientNameKey(p: PatientDto): string {
  return `${p.name.family ?? ""} ${p.name.given?.join(" ") ?? ""}`.trim().toLowerCase();
}

/**
 * Pure selector backing `usePatients({ careManagerId })`: patients assigned to
 * the practitioner (EpisodeOfCare.careManager) via at least one non-terminal
 * (non-discharged) case, active only, deduped and sorted by name.
 */
export function selectPatientsForCareManager(
  patients: PatientDto[],
  episodes: CaseAssignment[],
  careManagerId: string,
): PatientDto[] {
  if (!careManagerId) return [];

  const assignedIds = new Set<string>();
  for (const ep of episodes) {
    if (domainId(ep.careManager, PRACTITIONER_ENTITY_ID_SYSTEM) !== careManagerId) continue;
    if (ep.status && TERMINAL_CASE_STATUSES.has(ep.status)) continue;
    const pid = domainId(ep.patient, PATIENT_ENTITY_ID_SYSTEM);
    if (pid) assignedIds.add(pid);
  }

  return patients
    .filter((p) => p.active && assignedIds.has(p.patientId))
    .sort((a, b) => patientNameKey(a).localeCompare(patientNameKey(b)));
}

function pickPrimaryName(names: FhirHumanName[] | undefined): { primary: HumanName; others?: HumanName[] } {
  const selected = PatientModel.legalName(names);
  if (!names || !selected) return { primary: {} };
  const primaryIdx = names.indexOf(selected);
  const primary = normalizeName(names[primaryIdx]);
  const others = names.filter((_, i) => i !== primaryIdx).map(normalizeName);
  return others.length ? { primary, others } : { primary };
}

function normalizeName(n: FhirHumanName | undefined): HumanName {
  if (!n) return {};
  return {
    ...(n.use ? { use: n.use as HumanName["use"] } : {}),
    ...(n.prefix?.length ? { prefix: [...n.prefix] } : {}),
    ...(n.given?.length ? { given: [...n.given] } : {}),
    ...(n.family ? { family: n.family } : {}),
    ...(n.suffix?.length ? { suffix: [...n.suffix] } : {}),
  };
}

function normalizeTelecom(telecom: Patient["telecom"]): ContactPoint[] | undefined {
  if (!telecom?.length) return undefined;
  return telecom.map((t) => ({
    ...(t.system ? { system: t.system as ContactPoint["system"] } : {}),
    ...(t.value ? { value: t.value } : {}),
    ...(t.use ? { use: t.use as ContactPoint["use"] } : {}),
    ...(t.rank !== undefined ? { rank: t.rank } : {}),
  }));
}

function normalizeAddress(address: Patient["address"]): Address[] | undefined {
  if (!address?.length) return undefined;
  return address.map((a) => ({
    ...(a.use ? { use: a.use as Address["use"] } : {}),
    ...(a.type ? { type: a.type as Address["type"] } : {}),
    ...(a.line?.length ? { line: [...a.line] } : {}),
    ...(a.city ? { city: a.city } : {}),
    ...(a.state ? { state: a.state } : {}),
    ...(a.postalCode ? { postalCode: a.postalCode } : {}),
    ...(a.country ? { country: a.country } : {}),
  }));
}

function normalizeIdentifiers(identifier: Patient["identifier"]): Identifier[] | undefined {
  if (!identifier?.length) return undefined;
  return identifier.map((i) => ({
    ...(i.system ? { system: i.system } : {}),
    ...(i.value ? { value: i.value } : {}),
    ...(i.use ? { use: i.use as Identifier["use"] } : {}),
  }));
}

/**
 * Build a DTO from a single Patient resource — demographics + clinical state all
 * live on it (ADR-0006).
 */
function toDto(patient: PatientRow): PatientDto {
  const { primary, others } = pickPrimaryName(patient.name);
  const deceased =
    patient.deceasedBoolean !== undefined
      ? patient.deceasedBoolean
      : patient.deceasedDateTime !== undefined
        ? patient.deceasedDateTime
        : undefined;

  const telecom = normalizeTelecom(patient.telecom);
  const address = normalizeAddress(patient.address);
  const identifiers = normalizeIdentifiers(patient.identifier);

  return {
    patientId: patientIdSchema.parse(patient.domainId),
    active: patient.active ?? true,
    name: primary,
    ...(others ? { otherNames: others } : {}),
    ...(patient.gender ? { gender: patient.gender as PatientDto["gender"] } : {}),
    ...(patient.birthDate ? { birthDate: patient.birthDate } : {}),
    ...(deceased !== undefined ? { deceased } : {}),
    ...(telecom ? { telecom } : {}),
    ...(address ? { address } : {}),
    ...(identifiers ? { identifiers } : {}),
  };
}

interface UsePatientsParams {
  /**
   * Restrict to non-discharged patients assigned to this practitioner
   * (EpisodeOfCare.careManager). Omit for all patients.
   */
  careManagerId?: string;
}

interface UsePatientsResult {
  patients: PatientDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function usePatients(params?: UsePatientsParams): UsePatientsResult {
  const patients = useCollection(PATIENT_COLLECTION);
  const episodes = useCollection(EPISODE_OF_CARE_COLLECTION);
  const careManagerId = params?.careManagerId;

  const result = useLiveQuery({
    queryKey: [patients.id, "patients"],
    query: (q) =>
      q
        .from({ patient: patients })
        .fn.where(
          ({ patient }) => !patient.extension?.some((e) => e.url === PATIENT_HOLDING_URL && e.valueBoolean === true),
        )
        .fn.select(({ patient }) => toDto(patient)),
  });

  // Only subscribes when filtering by care manager — otherwise the query is a no-op.
  const assignedCases = useLiveQuery({
    queryKey: [episodes.id, "care-manager-cases", careManagerId ?? null],
    query: (q) => {
      if (!careManagerId) return undefined;
      return q
        .from({ eoc: episodes })
        .where(({ eoc }) =>
          and(
            eq(eoc.careManager?.identifier?.system, PRACTITIONER_ENTITY_ID_SYSTEM),
            eq(eoc.careManager?.identifier?.value, careManagerId),
          ),
        )
        .fn.select(({ eoc }) => eoc);
    },
  });

  const all = (result.data as PatientDto[] | undefined) ?? [];
  const data = careManagerId
    ? selectPatientsForCareManager(all, (assignedCases.data as EpisodeOfCare[] | undefined) ?? [], careManagerId)
    : all;

  return {
    patients: data,
    isLoading: result.isLoading || (careManagerId ? assignedCases.isLoading : false),
    isReady: result.isReady && (careManagerId ? assignedCases.isReady : true),
    isError: result.isError || (careManagerId ? assignedCases.isError : false),
  };
}

interface UsePatientResult {
  patient: PatientDto | null;
  isLoading: boolean;
  isReady: boolean;
}

/** Single patient by id. `patient` is null until ready or if not found. */
export function usePatient(patientId: string | undefined): UsePatientResult {
  const patients = useCollection(PATIENT_COLLECTION);

  const result = useLiveQuery({
    queryKey: [patients.id, "patient", patientId ?? null],
    query: (q) => {
      if (!patientId) return undefined;
      return q
        .from({ patient: patients })
        .where(({ patient }) => eq(patient.domainId, patientIdSchema.parse(patientId)))
        .fn.where(
          ({ patient }) => !patient.extension?.some((e) => e.url === PATIENT_HOLDING_URL && e.valueBoolean === true),
        )
        .fn.select(({ patient }) => toDto(patient))
        .findOne();
    },
  });

  return {
    patient: (result.data as PatientDto | undefined) ?? null,
    isLoading: result.isLoading,
    isReady: result.isReady,
  };
}
